import { buttonStyle } from 'components/buttons/buttonStyle';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { Button } from 'components/ui/button';
import { useInvoice } from 'pages/invoices/hooks/useInvoice';
import { useInvoiceDetails } from 'pages/invoices/hooks/useInvoiceDetails';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'store';
import { LimitType } from 'types/invoice-details';
import { InvoiceData } from 'types/invoices';

interface Props {
    invoice: InvoiceData;
    isInViewMode: boolean;
    onAddNewTimecards: (limit: LimitType, invoice: InvoiceData) => Promise<void>;
}

// Current-month timecards are normally added through the details tab's warning ribbon, which the
// NEW_TIMECARDS_WARNING_IGNORE_CURRENT_MONTH flag hides — this keeps the action reachable for the
// tenants that opted out of the warning. Null whenever that ribbon carries the action.
export const useAddNewTimecardsAction = ({ invoice, isInViewMode, onAddNewTimecards }: Props) => {
    const { openModal, closeModal } = useModal();
    const { isApproved, isPrepayment } = useInvoice(invoice);
    const { hasIgnoredCurrentMonthTimecards, isDetailsReadOnly } = useInvoiceDetails(invoice);

    if (!hasIgnoredCurrentMonthTimecards || isInViewMode || isDetailsReadOnly || isApproved || isPrepayment) {
        return null;
    }

    return () =>
        openModal(
            <ConfirmationModal
                titleId="invoices.actions.include-timecards"
                onCancel={closeModal}
                onConfirm={() => {
                    void onAddNewTimecards(LimitType.ALL, invoice);
                }}
                content={
                    <p className="text-body1">
                        <FormattedMessage id="invoices.actions.add-new-timecards-this-month-confirm" />
                    </p>
                }
                alert={{
                    titleId: 'invoices.actions.include-timecards',
                    description: <FormattedMessage id="invoices.actions.add-new-timecards-this-month-confirm" />,
                    confirmId: 'invoices.actions.include-timecards',
                }}
            />,
            closeModal,
            { width: 400 }
        );
};

/** The header's button for {@link useAddNewTimecardsAction}; renders nothing while the action is off. */
export const AddNewTimecardsButton = (props: Props) => {
    const buttonsDisabled = useSelector((state) => state.buttons.buttonsDisabled);
    const addNewTimecards = useAddNewTimecardsAction(props);

    if (!addNewTimecards) {
        return null;
    }

    return (
        <Button
            {...buttonStyle({ variant: 'outlined', color: 'secondary', size: 'small' })}
            disabled={buttonsDisabled}
            onClick={addNewTimecards}
        >
            <FormattedMessage id="invoices.actions.add-new-timecards" />
        </Button>
    );
};
